import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { LOG_KEY } from "../services/logger";
import { getSessionLog, clearSessionLog, trackAction } from "../services/sessionLogger";

const API_URL = "https://www.crush-now.fr/api/logs/client";

async function readPendingLogs() {
  try {
    if (Capacitor.isNativePlatform()) {
      const { value } = await Preferences.get({ key: LOG_KEY });
      return value ? JSON.parse(value) : [];
    }
    const raw = localStorage.getItem(LOG_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

async function clearPendingLogs() {
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key: LOG_KEY });
    } else {
      localStorage.removeItem(LOG_KEY);
    }
  } catch (_) {}
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch {
    return iso;
  }
}

const ReportBugButton = () => {
  const [open, setOpen] = useState(false);
  const [sessionLog, setSessionLog] = useState([]);
  const [pendingLogs, setPendingLogs] = useState([]);
  const [includeTech, setIncludeTech] = useState(false);
  const [messageLibre, setMessageLibre] = useState("");
  const [status, setStatus] = useState("idle");

  const handleOpen = async () => {
    trackAction("Signaler un probleme");
    const pending = await readPendingLogs();
    setPendingLogs(pending);
    setSessionLog(getSessionLog());
    setIncludeTech(false);
    setMessageLibre("");
    setStatus("idle");
    setOpen(true);
  };

  const handleSend = async () => {
    trackAction("Envoyer rapport bug");
    setStatus("sending");
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionLog,
          logs: includeTech ? pendingLogs : [],
          messageLibre: messageLibre.trim(),
        }),
      });
      if (res.ok) {
        clearSessionLog();
        await clearPendingLogs();
        setStatus("done");
        setTimeout(() => setOpen(false), 2000);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        style={{
          width: "100%", minHeight: "44px", fontSize: "14px", fontWeight: 600,
          background: "none", border: "1px solid #e2e8f0", borderRadius: "12px",
          cursor: "pointer", color: "#64748b", marginTop: "8px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
        }}
      >
        🐛 Signaler un problème
      </button>
    );
  }

  const modal = (
    <>
      <div
        onClick={() => setOpen(false)}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.48)", zIndex: 8999 }}
      />
      <div style={{
        position: "fixed", top: "50%", left: "16px", right: "16px",
        transform: "translateY(-50%)",
        zIndex: 9000, background: "white", borderRadius: "16px",
        maxWidth: "500px", maxHeight: "80vh",
        boxSizing: "border-box",
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 60px rgba(0,0,0,0.28)", overflow: "hidden",
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>
        {/* En-tete */}
        <div style={{
          position: "sticky", top: 0, background: "white", zIndex: 1,
          padding: "20px 20px 14px", borderBottom: "1px solid #e2e8f0",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "1.05rem", fontWeight: 900, color: "#0f172a" }}>
              🐛 Signaler un problème
            </h3>
            <p style={{ margin: "3px 0 0", fontSize: "0.75rem", color: "#94a3b8" }}>
              {pendingLogs.length > 0
                ? pendingLogs.length + " erreur(s) technique(s) capturee(s)"
                : "Aucune erreur technique detectee"}
            </p>
          </div>
          <button onClick={() => setOpen(false)} style={{
            background: "#F1F5F9", border: "none", borderRadius: "8px",
            padding: "6px 11px", cursor: "pointer", fontSize: "1rem", color: "#475569",
          }}>✕</button>
        </div>

        {/* Corps */}
        <div style={{ padding: "16px 20px", flex: 1, overflowY: "auto" }}>
          {status === "done" ? (
            <p style={{ textAlign: "center", color: "#16a34a", fontWeight: 700, fontSize: "15px", padding: "24px 0" }}>
              Merci, rapport envoye !
            </p>
          ) : (
            <>
              {/* Journal de navigation */}
              <p style={{ fontSize: "13px", color: "#475569", fontWeight: 600, margin: "0 0 6px" }}>
                Journal de navigation de cette session
              </p>
              <div style={{
                background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0",
                padding: "8px 10px", marginBottom: "14px",
                height: "120px", overflowY: "auto", overflowX: "hidden",
                width: "100%", boxSizing: "border-box",
                fontFamily: "monospace", fontSize: "11px", lineHeight: 1.7, color: "#475569",
                whiteSpace: "pre-wrap", wordBreak: "break-all",
              }}>
                {sessionLog.length === 0 ? (
                  <span style={{ color: "#94a3b8" }}>Aucune activite enregistree dans cette session</span>
                ) : (
                  sessionLog.map((entry, i) => (
                    <div key={i}>{formatTime(entry.timestamp)} — {entry.label}</div>
                  ))
                )}
              </div>

              {/* Case logs techniques */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
                <input
                  id="include-tech"
                  type="checkbox"
                  checked={includeTech}
                  onChange={(e) => setIncludeTech(e.target.checked)}
                  style={{ flexShrink: 0, width: "16px", height: "16px", cursor: "pointer" }}
                />
                <label
                  htmlFor="include-tech"
                  style={{ fontSize: "13px", color: "#475569", cursor: "pointer", margin: 0 }}
                >
                  Inclure les logs techniques (anonymes)
                  {pendingLogs.length > 0 && (
                    <span style={{ color: "#dc2626", marginLeft: "4px" }}>
                      — {pendingLogs.length} erreur(s)
                    </span>
                  )}
                </label>
              </div>

              {/* Detail erreurs techniques si case cochee */}
              {includeTech && pendingLogs.length > 0 && (
                <div style={{ marginBottom: "14px" }}>
                  {pendingLogs.map((log, i) => (
                    <div key={i} style={{
                      padding: "6px 10px", background: "#fef2f2", borderRadius: "8px",
                      marginBottom: "5px", border: "1px solid #fee2e2",
                    }}>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#dc2626", display: "block" }}>
                        {log.context}
                      </span>
                      <span style={{ fontSize: "12px", color: "#475569" }}>{log.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <p style={{ fontSize: "13px", color: "#475569", fontWeight: 600, margin: "0 0 6px" }}>
                Message (optionnel)
              </p>
              <textarea
                value={messageLibre}
                onChange={(e) => setMessageLibre(e.target.value)}
                placeholder="Decris ce qui s'est passe..."
                style={{
                  width: "100%", minHeight: "72px", padding: "10px",
                  border: "1px solid #e2e8f0", borderRadius: "8px",
                  fontSize: "13px", resize: "vertical", boxSizing: "border-box",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              />

              {status === "error" && (
                <p style={{ fontSize: "12px", color: "#dc2626", marginTop: "8px" }}>
                  Erreur lors de l'envoi. Verifie ta connexion et reessaie.
                </p>
              )}
            </>
          )}
        </div>

        {/* Pied */}
        {status !== "done" && (
          <div style={{
            position: "sticky", bottom: 0, background: "white",
            borderTop: "1px solid #e2e8f0", padding: "14px 20px",
            flexShrink: 0, display: "flex", gap: "10px",
          }}>
            <button onClick={() => setOpen(false)} style={{
              flex: 1, padding: "11px", borderRadius: "8px",
              border: "1px solid #e2e8f0", background: "white",
              color: "#64748b", fontWeight: 600, fontSize: "14px", cursor: "pointer",
            }}>
              Annuler
            </button>
            <button
              onClick={handleSend}
              disabled={status === "sending"}
              style={{
                flex: 2, padding: "11px", borderRadius: "8px", border: "none",
                background: status === "sending" ? "#94a3b8" : "linear-gradient(135deg, #FF8C00, #FF0080)",
                color: "white", fontWeight: 700, fontSize: "14px",
                cursor: status === "sending" ? "default" : "pointer",
              }}
            >
              {status === "sending" ? "Envoi..." : "Envoyer"}
            </button>
          </div>
        )}
      </div>
    </>
  );

  return createPortal(modal, document.body);
};

export default ReportBugButton;
